import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { OperationGateway } from 'src/app/application/gateway/OperationGateWay';
import { ProfileGateway } from 'src/app/application/gateway/ProfileGateway';
import { Exporter } from 'src/app/domain/models/Profile';
import { excelUtils } from 'src/app/ui/util/excel-utils';

@Component({
  selector: 'app-admin-assigned-documents-report',
  templateUrl: './admin-assigned-documents-report.component.html',
  styleUrls: ['./admin-assigned-documents-report.component.css']
})
export class AdminAssignedDocumentsReportComponent implements OnInit {
  loading: boolean = false;
  loadingBtnDownload: boolean = false;
  exporters: Exporter[] = [];

  minStartDate?: Date;
  maxStartDate?: Date;
  minEndDate?: Date;
  maxEndDate?: Date;

  form: FormGroup = this._formBuilder.group({
    exporterId: [null, Validators.required],
    startDate: [null, Validators.required],
    endDate: [null, Validators.required],
  });

  constructor(
    private _messageService: MessageService,
    private _formBuilder: FormBuilder,
    private _profileService: ProfileGateway,
    private _operationService: OperationGateway,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.getExporters();
  }

  getExporters() {
    this.loading = true;
    this._profileService.getAllExporters().subscribe({
      next: (v) => {
        this.exporters = v;
        this.loading = false;
      },
      error: (e) => {
        console.log(e);
        this.loading = false;
        this._messageService.add({
          severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Exporter.ManyRetrieveError'),
        });
      },
    });
  }

  // El rango puede estar dentro de un mismo año o abarcar dos años consecutivos.
  onStartDateChange() {
    const startDate: Date | null = this.form.controls['startDate'].value;
    this.minEndDate = startDate ?? undefined;
    this.maxEndDate = startDate ? new Date(startDate.getFullYear() + 1, 11, 31) : undefined;
  }

  onEndDateChange() {
    const endDate: Date | null = this.form.controls['endDate'].value;
    this.minStartDate = endDate ? new Date(endDate.getFullYear() - 1, 0, 1) : undefined;
    this.maxStartDate = endDate ?? undefined;
  }

  isValidDateRange(startDate: Date, endDate: Date): boolean {
    return startDate <= endDate && endDate.getFullYear() - startDate.getFullYear() <= 1;
  }

  formatDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  downloadReport() {
    if (this.form.invalid) return;

    const { exporterId, startDate, endDate } = this.form.value;

    if (!this.isValidDateRange(startDate, endDate)) {
      this._messageService.add({
        severity: 'error',
        summary: this.translate.instant('MessageService.Summary.FailedAction'),
        detail: this.translate.instant('MessageService.AssignedDocumentsReport.InvalidDateRange'),
      });
      return;
    }

    this.loadingBtnDownload = true;
    this._operationService
      .downloadDocumentAssignmentReport(exporterId, this.formatDate(startDate), this.formatDate(endDate))
      .subscribe({
        next: (resultBlob) => {
          excelUtils.downloadExcel(resultBlob.body, 'Assigned_Documents_Report');
          this.loadingBtnDownload = false;
        },
        error: (e) => {
          console.log(e);
          this.loadingBtnDownload = false;
          this._messageService.add({
            severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.AssignedDocumentsReport.DownloadError'),
          });
        },
      });
  }
}
